import {
  cloneElement,
  forwardRef,
  type InputHTMLAttributes,
  isValidElement,
  type ReactNode,
  type TextareaHTMLAttributes,
  useId,
} from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";

const baseInputClass =
  "block w-full rounded-md border border-ink-200 bg-surface text-ink-900 px-3 text-sm " +
  "placeholder:text-ink-400 transition-colors " +
  "enabled:hover:border-ink-300 " +
  "focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none " +
  "dark:focus:ring-accent-500/30 " +
  "disabled:bg-ink-50 disabled:text-ink-500 disabled:cursor-not-allowed " +
  "aria-invalid:border-rose-500 aria-invalid:ring-2 aria-invalid:ring-rose-500/20 " +
  "dark:aria-invalid:border-rose-400 dark:aria-invalid:ring-rose-400/30";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  iconLeft?: IconName;
  /** Optional suffix shown inside the field (e.g. "kr", "%"). */
  suffix?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, iconLeft, suffix, invalid, ...props }, ref) => {
    if (!iconLeft && !suffix) {
      return (
        <input
          ref={ref}
          className={cn(baseInputClass, "h-9", className)}
          aria-invalid={invalid}
          {...props}
        />
      );
    }
    // Wrapped variant — needed for icons/suffixes
    return (
      <div className="relative">
        {iconLeft && (
          <div className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-400">
            <Icon name={iconLeft} size="xs" />
          </div>
        )}
        <input
          ref={ref}
          className={cn(baseInputClass, "h-9", iconLeft && "pl-8", suffix && "pr-10", className)}
          aria-invalid={invalid}
          {...props}
        />
        {suffix && (
          <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 font-medium text-ink-500 text-xs">
            {suffix}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(baseInputClass, "min-h-[5rem] resize-y py-2", className)}
      aria-invalid={invalid}
      {...props}
    />
  ),
);
Textarea.displayName = "Textarea";

/**
 * Inline message under a form field — used for validation errors, hints, and
 * standalone form/mutation errors. Error variant fades in via the
 * `animate-fade-in` keyframe (defined in tailwind.config.js) and remounts
 * whenever the message text changes so a new error animates in again.
 */
export interface FieldMessageProps {
  tone?: "error" | "hint";
  children: ReactNode;
  className?: string;
}

export function FieldMessage({ tone = "hint", children, className }: FieldMessageProps) {
  if (children === undefined || children === null || children === false || children === "")
    return null;
  const isError = tone === "error";
  return (
    <p
      key={typeof children === "string" ? children : undefined}
      className={cn(
        "flex items-start gap-1.5 text-xs motion-safe:animate-fade-in",
        isError ? "text-rose-600 dark:text-rose-400" : "text-ink-500",
        className,
      )}
      role={isError ? "alert" : undefined}
    >
      {isError && (
        <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0" aria-hidden="true" />
      )}
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** Form field wrapper: label + input + optional hint/error. */
export interface FieldProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  children: ReactNode;
  /** ID to wire label/input/hint correctly. Auto-generated if omitted. */
  id?: string | undefined;
  className?: string | undefined;
}

export function Field({ label, hint, error, required, children, id, className }: FieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  // Wire `htmlFor` to the actual input by injecting `id` into the child;
  // a caller-supplied `id` wins.
  const wired =
    isValidElement<{ id?: string }>(children) && !children.props.id
      ? cloneElement(children, { id: fieldId })
      : children;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={fieldId} className="font-medium text-ink-800 text-sm">
        {label}
        {required && (
          <span className="ml-0.5 text-rose-600 dark:text-rose-400" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div>{wired}</div>
      <FieldMessage tone={error ? "error" : "hint"}>{error || hint}</FieldMessage>
    </div>
  );
}
